import type { SidebarMenuSelectedStyle } from "@/types/theme"

/**
 * Standardized spacing and styling constants for sidebar components.
 * Ensures consistent dividers, padding, and gaps across collapsed/expanded states.
 */
export const SIDEBAR_DIVIDER_CLASS = "border-t mx-2 my-2"
export const SIDEBAR_DIVIDER_TIGHT_CLASS = "border-t mx-2 my-1"
export const SIDEBAR_DIVIDER_NONE_CLASS = "border-t mx-2"

export const SIDEBAR_SECTION_PADDING_COLLAPSED = "px-2 py-3"
export const SIDEBAR_SECTION_PADDING_EXPANDED = "px-2 py-2"

/**
 * Shared 32px column for org avatar, nav icons, collapse / logo, and user avatar.
 * Pair with group/header/footer `p-2` so the glyph centre stays 24px from the rail edge
 * in both expanded and collapsed states.
 */
export const SIDEBAR_GLYPH_SLOT_CLASS = "flex size-8 shrink-0 items-center justify-center"

/** Selected-style variants for menu buttons */
export const SIDEBAR_SELECTED_BOXED = "bg-primary/10 text-primary border border-primary/20"
export const SIDEBAR_SELECTED_TEXT = "text-primary font-semibold border border-transparent"
export const SIDEBAR_SELECTED_ACCENT = "text-primary font-semibold border border-transparent border-l-2 border-l-primary ml-1 pl-1.5 -ml-0.5"
export const SIDEBAR_SELECTED_UNDERLINE = "text-primary font-medium border-b-2 border-b-primary border-transparent"
export const SIDEBAR_SELECTED_MINIMAL = "text-primary border border-transparent"

const SELECTED_STYLE_MAP: Record<SidebarMenuSelectedStyle, string> = {
  boxed: SIDEBAR_SELECTED_BOXED,
  text: SIDEBAR_SELECTED_TEXT,
  accent: SIDEBAR_SELECTED_ACCENT,
  underline: SIDEBAR_SELECTED_UNDERLINE,
  minimal: SIDEBAR_SELECTED_MINIMAL,
}

const ACTIVE_DATA_STYLE_MAP: Record<SidebarMenuSelectedStyle, string> = {
  boxed: "data-[active=true]:bg-primary/10 data-[active=true]:text-primary data-[active=true]:border data-[active=true]:border-primary/20",
  text: "data-[active=true]:text-primary data-[active=true]:font-semibold data-[active=true]:border data-[active=true]:border-transparent",
  accent: "data-[active=true]:text-primary data-[active=true]:font-semibold data-[active=true]:border data-[active=true]:border-transparent data-[active=true]:border-l-2 data-[active=true]:border-l-primary data-[active=true]:ml-1 data-[active=true]:pl-1.5 data-[active=true]:-ml-0.5",
  underline: "data-[active=true]:text-primary data-[active=true]:font-medium data-[active=true]:border-b-2 data-[active=true]:border-b-primary data-[active=true]:border-transparent",
  minimal: "data-[active=true]:text-primary data-[active=true]:border data-[active=true]:border-transparent",
}

export function getSidebarSelectedClasses(style?: SidebarMenuSelectedStyle | string | null): string {
  if (!style || !(style in SELECTED_STYLE_MAP)) {
    return SIDEBAR_SELECTED_BOXED
  }
  return SELECTED_STYLE_MAP[style as SidebarMenuSelectedStyle]
}

/** Data attribute classes for SidebarMenuButton active state */
export function getSidebarActiveDataClasses(style?: SidebarMenuSelectedStyle | string | null): string {
  if (!style || !(style in ACTIVE_DATA_STYLE_MAP)) {
    return ACTIVE_DATA_STYLE_MAP.boxed
  }
  return ACTIVE_DATA_STYLE_MAP[style as SidebarMenuSelectedStyle]
}
